import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import {DataTable} from "@/lib/components/DataTable";
import {RequestStatusByCompanyDTO} from "@/api/data-contracts";
import {CommonI18N, ReportI18N} from "@/translations";

interface RequestStatusByCompanyReportProps {
    data: RequestStatusByCompanyDTO[];
}

interface RequestStatusByCompanyRow extends RequestStatusByCompanyDTO {
    id: number;
}

/**
 * Report table showing request counts grouped by company and status.
 */
const RequestStatusByCompanyReport: React.FC<RequestStatusByCompanyReportProps> = ({ data }) => {
    const { t } = useTranslation();

    const rows = useMemo<RequestStatusByCompanyRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<RequestStatusByCompanyRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.company),
                accessorFn: (row) => row.companyName ?? "",
                id: "companyName"
            },
            {
                header: t(ReportI18N.page.column.status),
                accessorFn: (row) => (row.status ? t(CommonI18N.enum.requestStatus[row.status]) : ""),
                id: "status"
            },
            {
                header: t(ReportI18N.page.column.count),
                accessorFn: (row) => row.count ?? 0,
                id: "count"
            }
        ],
        [t]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default RequestStatusByCompanyReport;
